"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Zap, Building2, Settings, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const applications = [{
  id: "electrical",
  title: "Electrical Contractors and Panel Builders",
  description: "Electrical contractors use two-color acrylic engraving stock as an alternative to phenolic when specific color combinations are not available in phenolic laminate, or when the panel specification calls for acrylic. Evermark produces acrylic legend plates in 22mm, 30mm, and rectangular formats to the same tolerances as phenolic equivalents. Two-color acrylic nameplates meet the visual contrast requirements of UL 508A §49.6 for most panel identification applications — confirm with your inspector for dielectric-sensitive installations.",
  linkText: "View UL 508A Capabilities",
  linkUrl: "/PanelBuildersUL508APage"
}, {
  id: "facility",
  title: "Facility and Maintenance Engineers",
  description: "Facilities teams use laser-cut acrylic for room and door identification, equipment zone markers, safety sign panels, and directional signage throughout manufacturing plants, warehouses, and commercial buildings. Acrylic facility signs are more durable than vinyl-on-substrate signs and can be re-engraved or replaced individually without replacing the entire sign system. Evermark produces single replacement signs and full facility sign kits from the same price list.",
  linkText: "View Manufacturing Solutions",
  linkUrl: "/ManufacturingPage"
}, {
  id: "oem",
  title: "OEM Manufacturers and Product Designers",
  description: "OEM manufacturers order laser-cut acrylic for enclosure windows, display covers, instrument bezels, and custom-cut components that would otherwise require fabrication tooling. Evermark cuts from customer DXF files with ±0.005\" tolerance, producing parts ready for assembly without secondary finishing. No tooling cost. No minimum run size.",
  linkText: "View OEM Solutions",
  linkUrl: "/OEMEquipmentManufacturersPage"
}];
const getIcon = (id: string) => {
  switch (id) {
    case "electrical":
      return <Zap className="size-6 text-primary" />;
    case "facility":
      return <Building2 className="size-6 text-primary" />;
    case "oem":
      return <Settings className="size-6 text-primary" />;
    default:
      return null;
  }
};
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function IndustryApplications() {
  return <section id="industryapplications" className="bg-background py-24">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mb-16 max-w-3xl">
          <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground mb-6">
            {"Who Orders Acrylic Signs and Cutting from Evermark Industrial"}
          </h2>
          <div className="h-1 w-20 bg-primary rounded-md"></div>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {applications.map((app, i) => <motion.div key={app.id} data-index={i} variants={itemVariants} className="flex">
              <Card className="flex flex-col h-full border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300 w-full">
                <CardHeader className="pb-4">
                  <div className="mb-4 p-3 bg-background rounded-lg w-fit border border-border shadow-xs">
                    {getIcon(app.id)}
                  </div>
                  <CardTitle className="font-default text-xl text-card-foreground leading-tight">
                    {app.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow pt-0">
                  <p className="font-sans text-muted-foreground mb-8 flex-grow leading-relaxed">
                    {app.description}
                  </p>
                  
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}